<template>
  <v-dialog v-model="dialog" max-width="900" persistent>
    <v-card>
      <v-card-title class="headline">
        Importar participantes via CSV
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :disabled="processing.status === 'running'">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="step === 'upload'">
          <v-file-input
            v-model="file"
            label="Selecione o arquivo CSV"
            accept=".csv"
            prepend-icon="mdi-file-delimited"
            outlined
            @change="onFileSelected"
          ></v-file-input>

          <v-btn text color="primary" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Baixar modelo CSV
          </v-btn>

          <div v-if="csvData.length > 0" class="mt-4">
            <v-alert type="info" dense>
              {{ csvData.length }} linhas encontradas
            </v-alert>

            <v-simple-table dense class="mt-2">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Telefone</th>
                    <th>Pacote</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in csvData.slice(0, 5)" :key="i">
                    <td>{{ row['Nome Completo'] }}</td>
                    <td>{{ row['CPF'] }}</td>
                    <td>{{ row['Telefone'] }}</td>
                    <td>{{ row['Arrecadação (pacote)'] }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>

        <div v-if="step === 'processing'">
          <v-progress-linear
            :value="(processing.current / processing.total) * 100"
            height="25"
            color="primary"
          >
            <strong>{{ processing.current }} / {{ processing.total }}</strong>
          </v-progress-linear>

          <div class="mt-4">
            <v-chip small color="success" class="mr-2">
              ✓ {{ results.success.length }} sucesso
            </v-chip>
            <v-chip small color="warning" class="mr-2">
              ⚠ {{ results.warnings.length }} avisos
            </v-chip>
            <v-chip small color="error">
              ✗ {{ results.errors.length }} erros
            </v-chip>
          </div>

          <div class="mt-4 pa-3 grey lighten-4" style="max-height: 200px; overflow-y: auto;">
            <div v-for="(log, i) in activityLog.slice(-10)" :key="i" class="text-caption">
              {{ log }}
            </div>
          </div>
        </div>

        <div v-if="step === 'results'">
          <v-alert type="success" v-if="results.errors.length === 0">
            Importação concluída com sucesso!
          </v-alert>
          <v-alert type="warning" v-else>
            Importação concluída com {{ results.errors.length }} erro(s)
          </v-alert>

          <v-expansion-panels class="mt-4">
            <v-expansion-panel v-if="results.success.length > 0">
              <v-expansion-panel-header>
                ✓ Sucessos ({{ results.success.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Nome</th>
                        <th>Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in results.success" :key="item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.details }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel v-if="results.warnings.length > 0">
              <v-expansion-panel-header>
                ⚠ Avisos ({{ results.warnings.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Nome</th>
                        <th>Mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in results.warnings" :key="item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.message }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel v-if="results.errors.length > 0">
              <v-expansion-panel-header>
                ✗ Erros ({{ results.errors.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Nome</th>
                        <th>Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in results.errors" :key="item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.name }}</td>
                        <td class="error--text">{{ item.message }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close" v-if="step !== 'processing'">
          Fechar
        </v-btn>
        <v-btn
          color="primary"
          @click="startProcessing"
          :disabled="csvData.length === 0 || processing.status === 'running'"
          v-if="step === 'upload'"
        >
          Processar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import XLSX from 'xlsx'
import { mapMutations } from 'vuex'

export default {
  name: 'CSVImportFundraisingDialog',
  props: {
    visible: { type: Boolean, required: true },
    celebrationId: { type: String, required: true },
    fundraisingId: { type: String, required: false }
  },
  data: () => ({
    dialog: false,
    step: 'upload',
    file: null,
    csvData: [],
    fundraisingsCache: {},
    processing: {
      current: 0,
      total: 0,
      status: 'idle'
    },
    results: {
      success: [],
      warnings: [],
      errors: []
    },
    activityLog: []
  }),
  methods: {
    ...mapMutations(['SET_SNACKBAR']),

    onFileSelected (file) {
      if (!file) {
        this.csvData = []
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array', codepage: 65001 })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          this.csvData = XLSX.utils.sheet_to_json(firstSheet, { raw: false, defval: '' })

          if (this.csvData.length === 0) {
            this.SET_SNACKBAR({ text: 'Arquivo CSV vazio' })
          }
        } catch (error) {
          this.SET_SNACKBAR({ text: 'Erro ao ler arquivo CSV: ' + error.message })
          this.csvData = []
        }
      }
      reader.readAsArrayBuffer(file)
    },

    downloadTemplate () {
      const template = [
        ['Nome Completo', 'Telefone', 'E-mail', 'CPF', 'É comissão?', 'Dia vencimento', 'Arrecadação (pacote)', 'Meta', 'Parcelas restantes', 'Valor pago', 'Competencia pago', 'Valor atrasado', 'Competencia atrasado', 'Vencimento atrasado'],
        ['João Silva', '81999999999', 'joao@email.com', '12345678900', 'Não', '5', 'Nome do Pacote', '10000', '12', '500', '31/01/2026', '', '', '']
      ]
      const ws = XLSX.utils.aoa_to_sheet(template)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Modelo')
      XLSX.writeFile(wb, 'modelo_importacao_arrecadacoes.csv')
    },

    async startProcessing () {
      this.step = 'processing'
      this.processing = {
        current: 0,
        total: this.csvData.length,
        status: 'running'
      }
      this.results = { success: [], warnings: [], errors: [] }
      this.activityLog = []

      await this.loadFundraisings()

      for (let i = 0; i < this.csvData.length; i++) {
        const row = this.csvData[i]
        const rowNumber = i + 2

        this.addLog(`Processando linha ${rowNumber}: ${row['Nome Completo']}`)

        try {
          await this.processRow(row, rowNumber)
        } catch (error) {
          console.error('Erro na linha ' + rowNumber + ':', error.response ? error.response.data : error)
          let errorMessage = error.message || 'Erro desconhecido'
          if (error.response && error.response.data) {
            const data = error.response.data
            if (typeof data === 'string') {
              errorMessage = data
            } else if (data.message) {
              errorMessage = data.message
            } else if (data.errors) {
              errorMessage = Object.values(data.errors).flat().join(', ')
            } else if (data.error) {
              errorMessage = data.error
            } else {
              errorMessage = JSON.stringify(data)
            }
          }
          this.results.errors.push({
            row: rowNumber,
            name: row['Nome Completo'] || 'N/A',
            message: errorMessage
          })
          this.addLog(`❌ Linha ${rowNumber}: ${errorMessage}`)
        }

        this.processing.current = i + 1
      }

      this.processing.status = 'completed'
      this.step = 'results'
      this.$emit('imported')
    },

    async processRow (row, rowNumber) {
      const validation = this.validateRow(row)
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '))
      }

      console.log('Dados da linha:', row)

      const customerId = await this.createOrGetCustomer(row, rowNumber)
      console.log('Customer ID:', customerId)

      const participantId = await this.createOrGetParticipant(customerId, row, rowNumber)
      console.log('Participant ID:', participantId)

      let joinedId = null
      if (row['Arrecadação (pacote)']) {
        console.log('Criando joined...')
        joinedId = await this.createJoined(participantId, row, rowNumber)
        console.log('Joined ID:', joinedId)
      }

      if (joinedId && row['Valor pago'] && parseFloat(row['Valor pago']) > 0) {
        console.log('Criando fatura paga...')
        await this.createPaidInvoice(participantId, joinedId, row, rowNumber)
      }

      if (joinedId && row['Valor atrasado'] && parseFloat(row['Valor atrasado']) > 0) {
        console.log('Criando fatura atrasada...')
        await this.createOverdueInvoice(participantId, joinedId, row, rowNumber)
      }

      this.results.success.push({
        row: rowNumber,
        name: row['Nome Completo'],
        details: 'Cliente, participante e faturas criados'
      })
      this.addLog(`✓ Linha ${rowNumber}: Concluído`)
    },

    validateRow (row) {
      const errors = []

      if (!row['Nome Completo'] || row['Nome Completo'].split(' ').length < 2) {
        errors.push('Nome completo deve ter nome e sobrenome')
      }
      if (!row['CPF']) {
        errors.push('CPF é obrigatório')
      } else if (!this.isValidCPF(row['CPF'])) {
        errors.push('CPF inválido')
      }
      if (!row['E-mail']) {
        errors.push('E-mail é obrigatório')
      }
      if (!row['Telefone']) {
        errors.push('Telefone é obrigatório')
      }

      return {
        valid: errors.length === 0,
        errors
      }
    },

    isValidCPF (cpf) {
      cpf = String(cpf).replace(/[^\d]/g, '')

      if (cpf.length !== 11) return false
      if (/^(\d)\1{10}$/.test(cpf)) return false

      let sum = 0
      for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i)
      }
      let digit = 11 - (sum % 11)
      if (digit >= 10) digit = 0
      if (digit !== parseInt(cpf.charAt(9))) return false

      sum = 0
      for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i)
      }
      digit = 11 - (sum % 11)
      if (digit >= 10) digit = 0
      if (digit !== parseInt(cpf.charAt(10))) return false

      return true
    },

    async loadFundraisings () {
      try {
        const response = await axios.get(`fundraisings?filter[celebration_id]=${this.celebrationId}`)
        if (response.data && response.data.data) {
          for (const f of response.data.data) {
            this.fundraisingsCache[f.name.toLowerCase().trim()] = {
              id: f.id,
              participants: [] // Será preenchido ao buscar detalhes
            }
          }
          // Carregar detalhes de cada arrecadação para obter participantes já inscritos
          for (const name in this.fundraisingsCache) {
            const fundraisingId = this.fundraisingsCache[name].id
            try {
              const detailResponse = await axios.get(`fundraisings/${fundraisingId}`)
              if (detailResponse.data && detailResponse.data.participants) {
                this.fundraisingsCache[name].participants = detailResponse.data.participants.map(p => p.id)
              }
            } catch (e) {
              // Ignora erro ao carregar detalhes
            }
          }
        }
      } catch (error) {
        this.addLog('⚠ Erro ao carregar arrecadações')
      }
    },

    async createOrGetCustomer (row, rowNumber) {
      const cpf = String(row['CPF']).replace(/[^\d]/g, '')

      try {
        const checkResponse = await axios.get(`customers?filter[q]=${cpf}`)
        if (checkResponse.data && checkResponse.data.data && checkResponse.data.data.length > 0) {
          this.results.warnings.push({
            row: rowNumber,
            name: row['Nome Completo'],
            message: 'Cliente já existe, usando cadastro existente'
          })
          this.addLog(`⚠ Linha ${rowNumber}: Cliente já existe`)
          return checkResponse.data.data[0].id
        }
      } catch (error) {
        // Customer doesn't exist, continue to create
      }

      const customerData = {
        name: row['Nome Completo'],
        nickname: row['Nome Completo'].split(' ')[0],
        person_document: cpf,
        person_type: 'PF',
        phone: String(row['Telefone']).replace(/[^\d]/g, ''),
        email: row['E-mail'],
        address: {
          street: 'Av. Gov. Agamenon Magalhães',
          number: '2939',
          complement: 'Sala 406',
          postal_code: '52021-220',
          neighborhood: 'Espinheiro',
          city: 'Recife',
          state: 'PE'
        }
      }

      const response = await axios.post('customers', customerData)
      this.addLog(`✓ Linha ${rowNumber}: Cliente criado`)
      return response.data.id
    },

    async createOrGetParticipant (customerId, row, rowNumber) {
      try {
        const checkResponse = await axios.get(
          `participants?filter[customer_id]=${customerId}&filter[celebration_id]=${this.celebrationId}`
        )
        if (checkResponse.data && checkResponse.data.data && checkResponse.data.data.length > 0) {
          this.addLog(`⚠ Linha ${rowNumber}: Participante já existe`)
          return checkResponse.data.data[0].id
        }
      } catch (error) {
        // Participant doesn't exist, continue to create
      }

      const participantData = {
        celebration_id: this.celebrationId,
        customer_id: customerId,
        role: row['É comissão?'] === 'Sim' ? 'organizer' : 'participant',
        payment_day: parseInt(row['Dia vencimento']) || 0
      }

      const response = await axios.post('participants', participantData)
      this.addLog(`✓ Linha ${rowNumber}: Participante adicionado à turma`)
      return response.data.id
    },

    async createJoined (participantId, row, rowNumber) {
      const packageName = row['Arrecadação (pacote)'].toLowerCase().trim()
      const fundraisingData = this.fundraisingsCache[packageName]

      if (!fundraisingData) {
        throw new Error(`Pacote "${row['Arrecadação (pacote)']}" não encontrado`)
      }

      // Verificar se participante já está inscrito nessa arrecadação
      if (fundraisingData.participants && fundraisingData.participants.includes(participantId)) {
        this.results.warnings.push({
          row: rowNumber,
          name: row['Nome Completo'],
          message: 'Participante já está inscrito nesta arrecadação'
        })
        this.addLog(`⚠ Linha ${rowNumber}: Participante já inscrito no pacote, pulando...`)
        return null
      }

      const joinedData = {
        fundraising_id: fundraisingData.id,
        participant_id: participantId,
        amount_to_pay: parseFloat(row['Meta']) || 0,
        number_of_installment: parseInt(row['Parcelas restantes']) || 1,
        method: 'monthly',
        period: 'ALL'
      }

      const response = await axios.post('joineds', joinedData)
      this.addLog(`✓ Linha ${rowNumber}: Adicionado ao pacote`)

      // Adicionar ao cache para evitar duplicatas nas próximas linhas do mesmo CSV
      fundraisingData.participants.push(participantId)

      return response.data.id
    },

    async createPaidInvoice (participantId, joinedId, row, rowNumber) {
      console.log('Data original do CSV (Competencia pago):', row['Competencia pago'], typeof row['Competencia pago'])
      const invoiceDate = this.parseDate(row['Competencia pago'])
      console.log('Data parseada:', invoiceDate.format('YYYY-MM-DD'), invoiceDate.isValid())

      const invoiceData = {
        celebration_id: this.celebrationId,
        participant_id: participantId,
        invoice_date: invoiceDate.format('YYYY-MM-DD'),
        payment_date: invoiceDate.format('YYYY-MM-DD'),
        due_date: invoiceDate.format('YYYY-MM-DD'),
        items: [{
          category: 'fundraising',
          joined_id: joinedId,
          description: `Valor pago - ${row['Arrecadação (pacote)']}`,
          installment_amount: parseFloat(row['Valor pago'])
        }]
      }

      console.log('Criando fatura paga:', invoiceData)
      await axios.post('invoices/paid/third-parties', invoiceData)
      this.addLog(`✓ Linha ${rowNumber}: Fatura paga registrada`)
    },

    async createOverdueInvoice (participantId, joinedId, row, rowNumber) {
      console.log('Data original (Competencia atrasado):', row['Competencia atrasado'])
      console.log('Data original (Vencimento atrasado):', row['Vencimento atrasado'])

      const invoiceDate = this.parseDate(row['Competencia atrasado'])
      const dueDate = this.parseDate(row['Vencimento atrasado'])

      // Se não tiver data de competência, usar a data de vencimento
      const finalInvoiceDate = invoiceDate.isValid() ? invoiceDate : dueDate

      if (!finalInvoiceDate.isValid()) {
        throw new Error('Data de competência ou vencimento atrasado inválida')
      }

      const invoiceData = {
        celebration_id: this.celebrationId,
        participant_id: participantId,
        invoice_date: finalInvoiceDate.format('YYYY-MM-DD'),
        due_date: dueDate.isValid() ? dueDate.format('YYYY-MM-DD') : finalInvoiceDate.format('YYYY-MM-DD'),
        items: [{
          category: 'fundraising',
          joined_id: joinedId,
          description: `Valor atrasado - ${row['Arrecadação (pacote)']}`,
          installment_amount: parseFloat(row['Valor atrasado'])
        }]
      }

      console.log('Criando fatura atrasada:', invoiceData)
      await axios.post('invoices', invoiceData)
      this.addLog(`✓ Linha ${rowNumber}: Fatura em atraso registrada`)
    },

    parseDate (dateStr) {
      if (!dateStr) return this.$moment()

      // Converter para string caso seja número
      dateStr = String(dateStr)

      // Tentar formato DD/MM/YYYY
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/')
        if (parts.length === 3) {
          return this.$moment(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`, 'YYYY-MM-DD')
        }
      }

      // Tentar formato YYYY-MM-DD
      if (dateStr.includes('-')) {
        return this.$moment(dateStr, 'YYYY-MM-DD')
      }

      // Se for número (serial do Excel), converter
      if (!isNaN(dateStr)) {
        return this.$moment(new Date((parseFloat(dateStr) - 25569) * 86400 * 1000))
      }

      return this.$moment()
    },

    addLog (message) {
      this.activityLog.push(`${this.$moment().format('HH:mm:ss')} - ${message}`)
    },

    close () {
      if (this.processing.status === 'running') return

      this.step = 'upload'
      this.file = null
      this.csvData = []
      this.processing = { current: 0, total: 0, status: 'idle' }
      this.results = { success: [], warnings: [], errors: [] }
      this.activityLog = []

      this.$emit('close')
    }
  },

  watch: {
    visible (val) {
      this.dialog = !!val
    },
    dialog (val) {
      if (!val) this.close()
    }
  }
}
</script>
